import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  ChevronRight,
  FileText,
  ClipboardList,
  Eye,
  Upload,
  Sparkles,
  Zap,
  Plus,
  X,
} from "lucide-react";

import ScoreItem from "../../components/Common/ScoreItem";
import { projectInfo } from "../../utils/info";
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";

export default function EditBug({ darkMode }) {
  const navigate = useNavigate();
  const { bugId } = useParams();
  
  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const inputBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";

  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const canEditBugs =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");
  
  // ✅ ADDED: Split loading states properly
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // ✅ ADDED: Snapshot to track if data actually changed
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    actual_result: "",
    expected_result: "",
    severity: "",
    priority: "",
    module: "",
    environment: "",
    assigned_to: [],
  });
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);
  const [projectMembersLoading, setProjectMembersLoading] = useState(false);
  const [projectMembersError, setProjectMembersError] = useState(false);
  
  const [steps, setSteps] = useState([{ id: 1, action: "" }]);

  // IMAGE STATES
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  // FETCH BUG DATA ON MOUNT
  useEffect(() => {
    const fetchBugDetails = async () => {
      try {
        setInitialLoading(true);
        const res = await axios.get(`https://api.testoraglobal.in/api/bug/id/${bugId}`);
        const data = res.data;

        const parsedFormData = {
          title: data.title || "",
          description: data.description || "",
          actual_result: data.actual_result || "",
          expected_result: data.expected_result || "",
          severity: data.severity || "",
          priority: data.priority || "",
          module: data.module || "",
          environment: data.environment || "",
          assigned_to: Array.isArray(data.assigned_to)
            ? data.assigned_to
            : data.assigned_to
              ? [data.assigned_to]
              : [],
        };

        setFormData(parsedFormData);

        let parsedSteps = [{ id: 1, action: "" }];
        if (data.steps && data.steps.length > 0) {
          parsedSteps = data.steps.map((step, i) => ({
            id: i + 1,
            action: typeof step === "string" ? step : step?.action || "",
          }));
          setSteps(parsedSteps);
        }

        if (data.images) {
          setExistingImages(data.images);
        }

        // Save exactly what we fetched to compare later
        setInitialSnapshot({
          formData: parsedFormData,
          steps: parsedSteps.map(s => s.action),
        });

      } catch (error) {
        console.error("Failed to fetch bug", error);
        toast.error("Failed to load bug details");
      } finally {
        setInitialLoading(false);
      }
    };

    if (bugId) fetchBugDetails();
  }, [bugId]);

  // ✅ NEW: Real-time detection of data changes vs the snapshot
  const isDataChanged = useMemo(() => {
    if (!initialSnapshot) return false;
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialSnapshot.formData);
    const stepsChanged = JSON.stringify(steps.map(s => s.action)) !== JSON.stringify(initialSnapshot.steps);
    return formChanged || stepsChanged;
  }, [formData, steps, initialSnapshot]);

  const hasImages = existingImages.length > 0 || newImages.length > 0;

  // ✅ ADDED: Added hasImages requirement similar to your updated create page
  React.useEffect(() => {
    const projectId = selectedProject?.project_id;
    if (!projectId) {
      setProjectMembers([]);
      return;
    }

    let isCurrent = true;
    setProjectMembers([]);
    setProjectMembersError(false);
    setProjectMembersLoading(true);

    axios.get(`https://api.testoraglobal.in/api/project/id/${projectId}`)
      .then((res) => {
        if (!res.data?.success || !res.data.project) {
          throw new Error(res.data?.message || "Failed to load project members");
        }

        const members = [
          ...(res.data.project.managers || []),
          ...(res.data.project.developers || []),
          ...(res.data.project.qa || []),
          ...(res.data.project.org_admins || []),
        ];
        const uniqueMembers = [];
        const seen = new Set();

        members.forEach((member) => {
          const userId = Number(member?.user_id);
          if (!member || !userId || seen.has(userId)) return;
          seen.add(userId);
          uniqueMembers.push({ ...member, user_id: userId });
        });

        if (isCurrent) {
          setProjectMembers(uniqueMembers.sort((a, b) => (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "")));
        }
      })
      .catch((error) => {
        console.error("Failed to load project members", error);
        if (isCurrent) {
          setProjectMembersError(true);
          toast.error("Failed to load project members");
        }
      })
      .finally(() => {
        if (isCurrent) setProjectMembersLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [selectedProject?.project_id]);

  const selectedAssigneeIds = React.useMemo(() => {
    const raw = formData.assigned_to;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
  }, [formData.assigned_to]);

  const selectedAssigneeNames = React.useMemo(() => {
    return projectMembers
      .filter((member) => selectedAssigneeIds.includes(Number(member.user_id)))
      .map((member) => member.full_name || member.email || `User ${member.user_id}`);
  }, [projectMembers, selectedAssigneeIds]);

  const toggleAssignee = (userId) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.assigned_to) ? prev.assigned_to : prev.assigned_to ? [prev.assigned_to] : [];
      const numericId = Number(userId);
      const exists = current.some((id) => Number(id) === numericId);

      return {
        ...prev,
        assigned_to: exists
          ? current.filter((id) => Number(id) !== numericId)
          : [...current, numericId],
      };
    });
  };

  const isAIReady =
    formData.title &&
    formData.description &&
    formData.actual_result &&
    formData.expected_result &&
    formData.severity &&
    formData.priority &&
    formData.module &&
    formData.environment &&
    selectedAssigneeIds.length > 0 &&
    steps.some((s) => s.action.trim()) &&
    hasImages; 

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleStepChange = (index, value) => {
    const updated = [...steps];
    updated[index].action = value;
    setSteps(updated);
  };

  const addStep = () => {
    setSteps([...steps, { id: Date.now(), action: "" }]);
  };

  const removeStep = (id) => {
    if (steps.length > 1) {
      setSteps(steps.filter((step) => step.id !== id));
    }
  };

  const validateForm = () => {
    if (!formData.title?.trim()) { toast.error("Bug Title is required"); return false; }
    if (!formData.description?.trim()) { toast.error("Bug Description is required"); return false; }
    if (!formData.actual_result?.trim()) { toast.error("Actual Result is required"); return false; }
    if (!formData.expected_result?.trim()) { toast.error("Expected Result is required"); return false; }
    if (!formData.severity) { toast.error("Please select Severity"); return false; }
    if (!formData.priority) { toast.error("Please select Priority"); return false; }
    if (!formData.module?.trim()) { toast.error("Module is required"); return false; }
    if (!formData.environment?.trim()) { toast.error("Environment is required"); return false; }
    if (selectedAssigneeIds.length === 0) { toast.error("Assigned To is required"); return false; }
    if (!steps.length || steps.every((step) => !step.action.trim())) {
      toast.error("Please add at least one step");
      return false;
    }
    return true;
  };

  // UPLOAD IMAGES
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (existingImages.length + newImages.length + files.length > 3) {
      toast.error("Maximum 3 images allowed total");
      return;
    }

    const mappedFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setNewImages([...newImages, ...mappedFiles]);
  };

  const removeExistingImage = (url) => {
    setImagesToDelete([...imagesToDelete, url]);
    setExistingImages(existingImages.filter((img) => img !== url));
  };

  const removeNewImage = (index) => {
    const updated = [...newImages];
    updated.splice(index, 1);
    setNewImages(updated);
  };

  // SUBMIT UPDATES
  const handleSubmit = async () => {
    if (!canEditBugs) return;
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      let finalImages = [...existingImages];

      // 1. Upload new images
      if (newImages.length > 0) {
        const formDataImg = new FormData();
        newImages.forEach((img) => formDataImg.append("images", img.file));
        formDataImg.append("project_id", String(selectedProject.project_id));

        const uploadRes = await axios.post(
          "https://api.testoraglobal.in/api/bug/upload-images",
          formDataImg
        );

        if (uploadRes.data.success || uploadRes.data.urls) {
           finalImages = [...finalImages, ...uploadRes.data.urls];
        }
      }

      // 2. Delete removed images from Cloudinary
      for (const url of imagesToDelete) {
        try {
          await axios.post("https://api.testoraglobal.in/api/bug/delete-image", {
            image_url: url,
          });
        } catch (err) {
          console.error("Failed to delete image from cloudinary", err);
        }
      }

      // 3. Format payload for update
      const payload = {
        ...formData,
        assigned_to: selectedAssigneeIds,
        steps: steps.map(s => s.action), // Converting to match backend expectation if needed
        images: finalImages,
      };

      await axios.put(`https://api.testoraglobal.in/api/bug/update/${bugId}`, payload);

      toast.success("Bug updated successfully");
      
      setTimeout(() => {
        navigate(`/bug-optimizer/view/${bugId}`); // Navigate back to the details page
      }, 1500);

    } catch (error) {
      console.error(error);
      toast.error("Failed to update bug");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    navigate(`/bug-optimizer/view/${bugId}`);
  };

  const handleAIEnhance = async () => {
    if (!formData.title && !formData.description) {
      toast.error("Enter some data first");
      return;
    }

    try {
      setIsEnhancing(true);

      const payload = {
        ...formData,
        steps,
        project_info: projectInfo,
        project_id: selectedProject?.project_id,
      };

      const res = await axios.post(
        "https://api.testoraglobal.in/api/ai/analyze",
        payload,
      );

      const aiData = res.data;

      const allowedSeverity = ["Critical", "High", "Medium", "Low"];
      const allowedPriority = ["P0", "P1", "P2", "P3"];

      setFormData((prev) => ({
        ...prev,
        title: aiData.title || prev.title,
        description: aiData.description || prev.description,
        actual_result: aiData.actual_result || prev.actual_result,
        expected_result: aiData.expected_result || prev.expected_result,
        severity: allowedSeverity.includes(aiData.severity)
          ? aiData.severity
          : prev.severity,
        priority: allowedPriority.includes(aiData.priority)
          ? aiData.priority
          : prev.priority,
        module: aiData.module || prev.module,
        environment: aiData.environment || prev.environment,
      }));

      if (Array.isArray(aiData.steps)) {
        setSteps(
          aiData.steps.map((step, i) => ({
            id: Date.now() + i, // Unique IDs for AI mapped steps
            action: typeof step === "string" ? step : step.action || "",
          }))
        );
      }

      toast.success("AI Enhanced Successfully");
    } catch (error) {
      console.error(error);
      toast.error("AI Enhance failed, try again");
    } finally {
      setIsEnhancing(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
         <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <div className="px-8 pb-12 max-w-300 mx-auto animate-in slide-in-from-right-4 duration-500">
        <button
          onClick={() => navigate(`/bug-optimizer/view/${bugId}`)}
          className="flex items-center gap-2 text-gray-500 hover:text-purple-500 transition-colors mb-6 text-sm font-medium cursor-pointer"
        >
          <ChevronRight size={16} className="rotate-180" />
          Back to Bug Details
        </button>

        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3">
               <h2 className="text-4xl font-bold tracking-tight">Edit Bug</h2>
               <span className="px-3 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-full text-xs font-bold">
                 {bugId}
               </span>
            </div>
            <p className="text-gray-500 mt-2">
              Update Bug details with AI-assisted clarity.
            </p>
            <div className={`mt-6 p-5 rounded-3xl border ${cardBg} ${borderColor}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm uppercase tracking-[0.24em] text-gray-400 mb-2">Project</p>
                  <p className="font-semibold text-sm text-white">
                    {selectedProject?.project_name || "Current Project"}
                  </p>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.24em] text-gray-400 mb-2">Reported By</p>
                  <p className="font-semibold text-sm text-white">
                    {currentUser?.user_name || "Current User"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDiscard}
              className={`px-5 py-2.5 rounded-xl text-sm transition cursor-pointer font-bold border ${borderColor} ${
                darkMode
                  ? "text-gray-400 hover:text-[#f43f5e] hover:bg-white/5"
                  : "text-gray-600 hover:text-[#f43f5e]"
              }`}
            >
              Discard
            </button>

            {canEditBugs && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || isEnhancing}
                className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 transition cursor-pointer text-white text-sm font-bold shadow-lg shadow-purple-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-sm space-y-6`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText size={18} className="text-purple-500" />
                General Information
              </h3>

              <div className="space-y-1">
                <label className="text-sm font-medium">Bug Title</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter bug title (e.g., Login button not working)"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Bug Description</label>
                <input
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Briefly describe the issue"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Actual Result</label>
                <input
                  name="actual_result"
                  value={formData.actual_result}
                  onChange={handleChange}
                  placeholder="What actually happened?"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Expected Result</label>
                <input
                  name="expected_result"
                  value={formData.expected_result}
                  onChange={handleChange}
                  placeholder="What was expected?"
                  className={`w-full px-4 py-3.5 rounded-2xl border ${inputBg} ${borderColor}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Severity</label>
                  <select
                    name="severity"
                    value={formData.severity}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  >
                    <option value="">Select severity</option>
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Priority</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  >
                    <option value="">Select priority</option>
                    <option>P0</option>
                    <option>P1</option>
                    <option>P2</option>
                    <option>P3</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Module</label>
                  <input
                    name="module"
                    value={formData.module}
                    onChange={handleChange}
                    placeholder="e.g., Login / Dashboard"
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Environment</label>
                  <input
                    name="environment"
                    value={formData.environment}
                    onChange={handleChange}
                    placeholder="e.g., UAT / Dev / Prod"
                    className={`w-full px-4 py-3 rounded-xl border ${inputBg} ${borderColor}`}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium">Assigned To</label>
                  <button
                    type="button"
                    onClick={() => setAssigneeModalOpen(true)}
                    className={`w-full px-4 py-3 rounded-xl border text-left ${inputBg} ${borderColor} ${selectedAssigneeIds.length > 0 ? "text-white" : "text-gray-400"}`}
                  >
                    {selectedAssigneeNames.length > 0
                      ? selectedAssigneeNames.join(", ")
                      : "Select project members"}
                  </button>
                </div>
              </div>
            </div>

            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor}`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ClipboardList size={18} className="text-purple-500" />
                Steps to Reproduce
              </h3>

              {steps.map((step, index) => (
                <div key={step.id} className="flex gap-3 mt-3">
                  <input
                    value={step.action}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder={`Step ${index + 1}`}
                    className={`flex-1 px-4 py-2 rounded-xl border ${inputBg} ${borderColor}`}
                  />

                  <button
                    onClick={() => removeStep(step.id)}
                    className="hover:text-[#f43f5e] transition cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              <button
                onClick={addStep}
                className="text-purple-500 text-sm flex items-center gap-2 mt-4 cursor-pointer hover:text-purple-400"
              >
                <Plus size={14} />
                Add Step
              </button>
            </div>

            <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-sm space-y-6`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Eye size={18} className="text-purple-500" />
                Visual Evidence
              </h3>

              <label className={`border-2 border-dashed ${borderColor} rounded-2xl p-10 flex flex-col items-center justify-center bg-gray-900/10 hover:bg-gray-900/30 transition-all cursor-pointer`}>
                <Upload size={24} className="text-purple-500 mb-3" />
                <p className="text-sm font-bold">Upload New Images</p>
                <p className="text-xs text-gray-500">Max 3 images total</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              <div className="flex gap-3 flex-wrap mt-4">
                {/* Render Existing Images */}
                {existingImages.map((url, index) => (
                  <div key={`existing-${index}`} className="relative group">
                    <img
                      src={url}
                      alt="existing"
                      onClick={() => setPreviewImage(url)}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-gray-600"
                    />
                    <button
                      onClick={() => removeExistingImage(url)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-lg cursor-pointer"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}

                {/* Render Newly Added Previews */}
                {newImages.map((img, index) => (
                  <div key={`new-${index}`} className="relative group">
                    <img
                      src={img.preview}
                      alt="preview"
                      onClick={() => setPreviewImage(img.preview)}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-green-500/50"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-[9px] text-center font-bold py-0.5 rounded-b-lg">
                      NEW
                    </div>
                    <button
                      onClick={() => removeNewImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-lg cursor-pointer"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`p-6 rounded-3xl border ${cardBg} ${borderColor}`}>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-4">
                Quality Score
              </h4>
              <ul className="space-y-3">
                <ScoreItem label="Description provided" checked={!!formData.description} />
                <ScoreItem label="Steps to reproduce" checked={steps.some((s) => s.action.trim())} />
                <ScoreItem label="Expected vs Actual" checked={formData.actual_result && formData.expected_result} />
                <ScoreItem label="Visual evidence" checked={hasImages} />
              </ul>
            </div>

            <div className={`p-6 rounded-3xl border border-purple-500/30 bg-purple-600/5 backdrop-blur-md relative overflow-hidden`}>
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sparkles size={60} className="text-purple-500" />
              </div>
              <h4 className="text-sm font-bold flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-purple-400" />
                AI Enhancer - To boost your bug quality
              </h4>
              <p className="text-xs text-gray-400 leading-relaxed mb-6">
                Our AI will analyze your bug details and optimize the title,
                description, steps, and even suggest potential severity and
                priority to make sure your bug gets the attention it deserves!
              </p>
              
              <button
                onClick={handleAIEnhance}
                disabled={!isAIReady || !isDataChanged || isEnhancing || isSubmitting}
                className={`w-full py-3 rounded-2xl transition text-xs font-bold shadow-lg flex items-center justify-center gap-2
                ${
                  isAIReady && isDataChanged && !isSubmitting
                    ? "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer shadow-purple-600/30"
                    : "bg-gray-600 text-gray-300 cursor-not-allowed opacity-50"
                }`}
              >
                {isEnhancing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap size={14} fill="currentColor" />
                    Analyze & Optimize
                  </>
                )}
              </button>
              <p className="text-xs font-bold text-gray-400 mt-3">
                NOTE: This button will be enabled once you modify any data, fill in the required
                fields, and upload visual evidence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {assigneeModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-3xl border ${cardBg} ${borderColor} p-6 shadow-2xl`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">Select Assignees</h3>
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
              {projectMembersLoading ? (
                <p className="text-sm text-gray-400">Loading project members...</p>
              ) : projectMembersError ? (
                <p className="text-sm text-red-400">Unable to load project members. Please try again later.</p>
              ) : projectMembers.length === 0 ? (
                <p className="text-sm text-gray-400">No project members found for this project.</p>
              ) : (
                projectMembers.map((member) => {
                  const memberId = Number(member.user_id);
                  const checked = selectedAssigneeIds.includes(memberId);
                  return (
                    <label key={memberId} className={`flex items-center justify-between w-full rounded-2xl border px-4 py-3 cursor-pointer transition ${checked ? "border-purple-500 bg-purple-500/10" : `${borderColor} ${inputBg}`}`}>
                      <div>
                        <div className="font-medium text-sm">{member.full_name || member.email || `User ${memberId}`}</div>
                        <div className="text-xs text-gray-400">{member.email || "Project member"}</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssignee(memberId)}
                        className="h-4 w-4 accent-purple-500"
                      />
                    </label>
                  );
                })
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className={`px-4 py-2 rounded-xl border ${borderColor} text-sm`}>
                Cancel
              </button>
              <button type="button" onClick={() => setAssigneeModalOpen(false)} className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold">
                Confirm Assignees
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <div className="relative">
             <img
               src={previewImage}
               alt="preview full"
               className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl object-contain"
             />
             <button 
                onClick={() => setPreviewImage(null)}
                className="absolute -top-4 -right-4 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition"
             >
                <X size={20} />
             </button>
          </div>
        </div>
      )}
    </>
  );
}